import { ReRenderCanvas } from "@/Utils/StateLib";

/** Writes ?topOfChart= for URL-driven subchart nav; falls back to direct SubChartSwitch
 *  for subcharts with no stable record id. Shared by SubchartsCard and ChartNavigatorPanel. */
export const handleSubChartClick = (doc, scm, subchartid, setRouteParam) => {
  const theSC = scm?.FindSubChart?.(subchartid);
  if (!theSC) return;
  const topRef = theSC.GetTopDBObjRef?.();
  if (topRef != null) {
    setRouteParam("topOfChart", String(topRef));
    return;
  }
  doc.SubChartSwitch(theSC);
  ReRenderCanvas();
};

/** Recursive subchart tree shaped for the storybook <Tree> (used by SubchartsCard).
 *  Skips filtered subcharts and their subtrees. */
export const buildSCTree = currentSubChart => {
  const children = currentSubChart.GetChildren();
  if (!children) {
    return null;
  }

  const data = [];
  for (const subChart of children) {
    if (subChart.GetTopDBObj() && subChart.GetTopDBObj().IsFiltered()) {
      continue;
    }
    data.push({
      label: subChart.GetSubChartName(),
      value: subChart.GetSubChartID(),
      subTree: buildSCTree(subChart)
    });
  }

  return data;
};

/** "ACRONYM - Full Division Name" for a subchart. The acronym is the subchart name; the
 *  full name arrives with the subchart payload (scFullName) so it's available synchronously
 *  for every row — no lazy top-box load. Falls back to the acronym alone when the full name
 *  is missing or identical (some charts key the subchart name on the title field itself). */
export const getSubchartLabel = subChart => {
  const acronym = subChart.GetSubChartName();
  const fullName = subChart.GetSubChartFullName?.() ?? "";
  return fullName && fullName !== acronym ? `${acronym} - ${fullName}` : acronym;
};

/** Flattens the subchart tree (root + all descendants, pre-order) into a flat list for a
 *  searchable navigator. Skips filtered subcharts and their subtrees, matching buildSCTree. */
export const flattenSubchartTree = (subChart, depth = 0, out = []) => {
  if (!subChart) return out;

  const topObj = subChart.GetTopDBObj?.();
  if (topObj && topObj.IsFiltered && topObj.IsFiltered()) {
    return out;
  }

  out.push({
    label: getSubchartLabel(subChart),
    value: subChart.GetSubChartID(),
    topRef: subChart.GetTopDBObjRef?.(),
    depth
  });

  const children = subChart.GetChildren?.() || [];
  for (const child of children) {
    flattenSubchartTree(child, depth + 1, out);
  }

  return out;
};
